import React, { useRef, useState } from 'react';
import { Compass, BadgeCheck, Receipt, Users } from 'lucide-react';
import { Wrapper, HeaderBar, ScheduleCountdown, HostRow, TrustStrip, TicketLine, CtaButton, Footer, GreenCheck, PendingDot, Globe, Stars, VerifiedTag, WEEK_LABEL, H1, H2, LEAD, CARD } from './Shared';
import { PhoneInput } from './PhoneInput';
import { COUNTRIES, Country } from './countries';
import { COLLAGE_URL } from './config';
import { track, sendLead, saveLead } from './pixel';

// Testimonios: SOLO reales y con autorización por escrito
const TESTIMONIALS = [1, 2, 3].map(n => ({
  name: `[Nombre ${n}.]`,
  country: '[Estado]',
  title: '[Título del testimonio]',
  body: '[Dónde estaba antes, por qué decidió hablar con Chale y qué avance concreto logró (licencia, primer cliente, etc.).]'
}));

// Reto de Chale en tiempo real: marca done=true a medida que avanza
const JOURNEY = [{
  label: 'Empecé mi pre-licencia',
  done: true
}, {
  label: 'Aprobar mi examen de licencia',
  done: false
}, {
  label: 'Mi primer cliente',
  done: false
}, {
  label: 'Mi primer workshop en vivo',
  done: false
}];
const ICON = 'w-7 h-7 text-brand';
const LEARN = [{
  icon: <Compass className={ICON} strokeWidth={1.8} data-id="element-541" data-mp="1weatgo63gjvj" />,
  t: 'Qué hacemos exactamente, sin rodeos',
  p: 'Cómo ayudamos a familias latinas a proteger su dinero con seguros de vida, y cómo es el trabajo en el día a día.'
}, {
  icon: <BadgeCheck className={ICON} strokeWidth={1.8} data-id="element-542" data-mp="2k1977e2txdpr" />,
  t: 'El camino exacto a tu licencia',
  p: 'Los pasos de la pre-licencia y el examen en tu estado, y cómo te acompañamos para que no lo hagas sola o solo.'
}, {
  icon: <Receipt className={ICON} strokeWidth={1.8} data-id="element-543" data-mp="2aaxsqxlirm4m" />,
  t: 'Costos y requisitos reales',
  p: 'Lo que cuesta empezar y lo que necesitas, explicado con claridad antes de que tomes cualquier decisión.'
}, {
  icon: <Users className={ICON} strokeWidth={1.8} data-id="element-544" data-mp="32saa2d72ltel" />,
  t: 'Cómo crecer y formar tu equipo',
  p: 'Cómo se construye esto poco a poco, a tu ritmo, cuando tus resultados lo permitan.'
}];
function validPhone(digits: string, c: Country) {
  const d = digits.replace(/\D/g, '');
  return d.length >= c.min && d.length <= c.max;
}
const SECTION = 'py-16 sm:py-24';
const input = 'w-full rounded-xl border border-gray-200 bg-white px-5 py-4 sm:py-[18px] text-[16px] sm:text-[18px] text-ink outline-none transition-[border-color,box-shadow] duration-150 ease-out focus:border-brand focus:ring-4 focus:ring-brand-light placeholder:text-gray-400';
export function Registration({
  onSuccess
}: {
  onSuccess: () => void;
}) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [country, setCountry] = useState<Country>(COUNTRIES[0]);
  const [err, setErr] = useState('');
  const [sending, setSending] = useState(false);
  const formRef = useRef<HTMLDivElement>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const scrollToForm = () => {
    formRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    });
    setTimeout(() => nameRef.current?.focus({
      preventScroll: true
    }), 500);
  };

  // PASO 1 (micro-compromiso): solo nombre, email y WhatsApp. Las preguntas de filtro van en el paso 2.
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validPhone(phone, country)) {
      setErr('Por favor ingresa un número válido.');
      return;
    }
    setErr('');
    setSending(true);
    const e164 = `+${country.dial}${phone.replace(/\D/g, '')}`;
    saveLead({
      name,
      email,
      phone: e164
    });
    await sendLead({
      step: 'datos',
      name,
      email,
      phone: e164,
      country: country.iso.toUpperCase(),
      source: window.location.href
    });
    track('Lead');
    setSending(false);
    onSuccess();
  };
  return <div className="bg-white" data-id="element-545" data-mp="evh0bbv90s5k">
      <HeaderBar data-id="element-546" data-mp="ev77lgitk6w6" />

      {/* HERO */}
      <section className="pt-10 sm:pt-16 pb-16 sm:pb-24" data-id="element-547" data-mp="2lklu0m2ke61l">
        <Wrapper className="text-center" data-id="element-548" data-mp="2ll5d463yb0h7">
          <div className="inline-flex items-center gap-2.5 rounded-full bg-brand-light px-4 py-1.5" data-id="element-549" data-mp="2gm2aesbg16ce">
            <span className="pulse-dot" data-id="element-550" data-mp="17pdgoskey1ps" />
            <span className="font-semibold text-brand text-[13px] sm:text-[15px]" data-id="element-551" data-mp="17oc4gkrnv96h">Llamada 1:1 gratis con Chale</span>
          </div>

          <h1 className={`${H1} mt-6 max-w-[920px] mx-auto`} data-id="element-552" data-mp="1cproqam0lbnt">
            Empieza tu propio negocio en seguros de vida, <span className="text-brand" data-id="element-553" data-mp="1lon0e27tvdyi">con acompañamiento paso a paso</span>
          </h1>

          <p className={`${LEAD} mt-6 max-w-[720px] mx-auto`} data-id="element-554" data-mp="oycm0gqbssc1">
            Te muestro el camino real que yo estoy siguiendo: tu licencia, el entrenamiento en ventas y cómo crecer a tu ritmo. Sin promesas exageradas y con claridad sobre costos y requisitos.
          </p>

          <div ref={formRef} className={`${CARD} mt-10 max-w-[620px] mx-auto p-6 sm:p-10 text-left`} data-id="element-555" data-mp="xxraxbquguuf">
            <p className="text-center text-[14px] sm:text-[15px] font-semibold text-brand" data-id="element-556" data-mp="12rvi9w40v4w1">{WEEK_LABEL}</p>
            <h2 className="mt-2 text-center font-extrabold text-ink tracking-tight text-[22px] sm:text-[28px] leading-tight" data-id="element-557" data-mp="65s41uc607n0">
              ¿A dónde te envío el acceso para elegir tu horario?
            </h2>

            <form id="regForm" onSubmit={submit} className="mt-6 space-y-3.5 sm:space-y-4" data-id="element-558" data-mp="2nkw8ol1sx33">
              <label htmlFor="reg-name" className="sr-only" data-id="element-559" data-mp="3tufcvus8ka5a">Nombre completo</label>
              <input id="reg-name" ref={nameRef} className={input} type="text" name="name" placeholder="Nombre completo" required autoComplete="name" value={name} onChange={e => setName(e.target.value)} data-id="element-560" data-mp="3pstujtwxn3np" />
              <label htmlFor="reg-email" className="sr-only" data-id="element-561" data-mp="3fyk108w97zbe">Correo electrónico</label>
              <input id="reg-email" className={input} type="email" name="email" placeholder="Correo electrónico" required autoComplete="email" inputMode="email" value={email} onChange={e => setEmail(e.target.value)} data-id="element-562" data-mp="3b4fqjfns595k" />
              <PhoneInput value={phone} onChange={v => {
              setPhone(v);
              if (err) setErr('');
            }} country={country} onCountryChange={setCountry} error={err} data-id="element-563" data-mp="3239994db3ub9" />
            </form>

            <div className="mt-6" data-id="element-564" data-mp="2s7obxjj6ni7u">
              <CtaButton type="submit" form="regForm" disabled={sending} label={sending ? 'Enviando…' : undefined} data-id="element-565" data-mp="2ijaf98gt6fa6" />
            </div>
            <p className="mt-4 text-center text-[13px] text-gray-500" data-id="element-566" data-mp="2njeteru8uxx8">Paso 1 de 2 · Toma menos de 60 segundos</p>
          </div>

          <ScheduleCountdown data-id="element-567" data-mp="28mxke79j1vdu" />
          <HostRow data-id="element-568" data-mp="2dk7j6dit3jec" />
          <TrustStrip data-id="element-569" data-mp="2djnzar9likxo" />
        </Wrapper>
      </section>

      {/* STORY */}
      <section className={`${SECTION} bg-[#F7F8FB]`} data-id="element-570" data-mp="319s0yt384am1">
        <Wrapper className="text-center" data-id="element-571" data-mp="31aa7qjnii787">
          <h2 className={H2} data-id="element-572" data-mp="368v6sfr12kv2">Seamos honestos desde el principio: <span className="text-brand" data-id="element-573" data-mp="368bpn197c0i3">yo también estoy empezando.</span></h2>
          <p className={`${LEAD} mt-6 max-w-[760px] mx-auto`} data-id="element-574" data-mp="2rkqk70outp70">[HISTORIA DE CHALE: de dónde viene, su trabajo en ventas y estética, qué no le gustaba y por qué decidió construir algo propio en servicios financieros.]</p>

          <div className={`${CARD} mt-12 max-w-[560px] mx-auto p-6 sm:p-8 text-left`} data-id="element-575" data-mp="3k730mesrp620">
            <p className="font-extrabold text-ink text-[20px] sm:text-[24px]" data-id="element-576" data-mp="3p0e72eto3kte">Mi reto, en tiempo real</p>
            <ul className="mt-5 space-y-4" data-id="element-577" data-mp="1dj2neok76jip">
              {JOURNEY.map(j => <li key={j.label} className={`flex items-center gap-3.5 font-semibold text-[16px] sm:text-[18px] ${j.done ? 'text-ink' : 'text-gray-400'}`} data-id="element-578" data-mp="18m2em5vyr2nw">
                  {j.done ? <GreenCheck className="w-7 h-7 shrink-0" data-id="element-579" data-mp="1ib3zoahd8a8q" /> : <PendingDot className="w-7 h-7 shrink-0" data-id="element-580" data-mp="1iceydvvyersz" />}
                  <span data-id="element-581" data-mp="ul9ixhtxe1j7">{j.label}</span>
                  {j.done && <span className="ml-auto rounded-full bg-brand-light px-2.5 py-0.5 text-[12px] font-semibold text-brand" data-id="element-582" data-mp="um1e4rjm1x8y">Logrado</span>}
                </li>)}
            </ul>
          </div>

          <div className="mt-12 mx-auto max-w-[720px]" data-id="element-583" data-mp="13nived8r84z8">
            {COLLAGE_URL ? <img src={COLLAGE_URL} alt="Chale trabajando" width={1440} height={810} loading="lazy" decoding="async" className="w-full h-auto aspect-[16/9] object-cover rounded-2xl" data-id="element-584" data-mp="zjjkca68tyd9" /> : <div className="aspect-[16/9] rounded-2xl bg-brand-light flex items-center justify-center text-[13px] font-semibold text-brand/70" data-id="element-585" data-mp="2b0jaaz54c144">[COLLAGE DE CHALE]</div>}
          </div>

          <p className="mt-12 max-w-[760px] mx-auto font-bold text-[19px] sm:text-[24px] leading-snug text-ink" data-id="element-586" data-mp="dbdym7one0hs">
            En la llamada te voy a mostrar <span className="hl-yellow" data-id="element-587" data-mp="i977x9l8zama">exactamente el camino que estoy siguiendo</span>, para que tú también puedas empezar.
          </p>
        </Wrapper>
      </section>

      {/* TESTIMONIALS (solo reales) */}
      <section className={SECTION} data-id="element-588" data-mp="r5y89y6u1bmo">
        <Wrapper className="text-center" data-id="element-589" data-mp="w1vqka1nos23">
          <h2 className={H2} data-id="element-590" data-mp="w0u6xh6kqx8r">Y no lo estoy haciendo sola: <span className="text-brand" data-id="element-591" data-mp="1118g52jwwd8d">estoy formando mi equipo</span></h2>
          <TrustStrip withTicket={false} data-id="element-592" data-mp="160uph0wmd9bw" />

          <div className="mt-12 grid gap-5 sm:gap-6 lg:grid-cols-3 text-left" data-id="element-593" data-mp="15ztbooo7y8qh">
            {TESTIMONIALS.map((t, i) => <article key={i} className={`${CARD} flex flex-col p-6 sm:p-7`} data-id="element-594" data-mp="32uza9jtvp342" data-mpi={i}>
                <div className="flex items-center gap-3" data-id="element-595" data-mp="37t0tq5v7v4g2">
                  <div className="w-12 h-12 rounded-full bg-brand-light shrink-0" aria-hidden="true" data-id="element-596" data-mp="1owuze33skc4c" />
                  <div data-id="element-597" data-mp="1k6mvc0wvxdof">
                    <h3 className="font-bold text-[17px] text-ink leading-tight" data-id="element-598" data-mp="1k5bti6n02fzy">{t.name}</h3>
                    <p className="mt-0.5 flex items-center gap-1.5 text-[13px] text-gray-500" data-id="element-599" data-mp="1g3zz7nt6l6lf"><Globe className="w-3.5 h-3.5" strokeWidth={2} data-id="element-600" data-mp="1b5yih9j89jd4" />{t.country}</p>
                  </div>
                </div>
                <div className="mt-4" data-id="element-601" data-mp="27uxsaszgmt7f"><Stars size={16} data-id="element-602" data-mp="27vfndmadz1dr" /></div>
                <h4 className="mt-3 font-bold text-[18px] leading-snug text-ink" data-id="element-603" data-mp="23ududn72blk8">{t.title}</h4>
                <p className="mt-2 text-[15px] leading-relaxed text-gray-600" data-id="element-604" data-mp="1yvaqw76qdned">{t.body}</p>
                <div className="mt-auto pt-5" data-id="element-605" data-mp="1yx3ybop64bnf"><VerifiedTag label="Miembro verificado del equipo" data-id="element-606" data-mp="1twpoc140armr" /></div>
              </article>)}
          </div>

          <p className="mt-14 max-w-[760px] mx-auto font-bold text-[19px] sm:text-[24px] leading-snug text-ink" data-id="element-607" data-mp="ngx6xdf3ls3m">
            En nuestra llamada te muestro el proceso completo paso a paso para que decidas <span className="hl-yellow" data-id="element-608" data-mp="g2zx5hl8vwlt">con toda la información.</span>
          </p>
          <div className="mt-8" data-id="element-609" data-mp="b3mze1su554g"><CtaButton onClick={scrollToForm} data-id="element-610" data-mp="b46hpjmedvj0" /></div>
          <div className="mt-5" data-id="element-611" data-mp="prrllfr3jyrz"><TicketLine data-id="element-612" data-mp="ps9mwqsm936r" /></div>
        </Wrapper>
      </section>

      {/* LEARN */}
      <section className={`${SECTION} bg-[#F7F8FB]`} data-id="element-613" data-mp="l1b8jwr6uf65">
        <Wrapper data-id="element-614" data-mp="kzgi62x5duuo">
          <h2 className={H2} data-id="element-615" data-mp="kyyj6yj3p2j2">Esto es lo que vas a descubrir en la llamada</h2>
          <div className="mt-12 grid gap-5 sm:gap-6 sm:grid-cols-2" data-id="element-616" data-mp="yqv52ku6eg70">
            {LEARN.map(({
            icon,
            t,
            p
          }) => <article key={t} className={`${CARD} p-6 sm:p-8`} data-id="element-617" data-mp="tvfrzv3zsobn">
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-brand-light" data-id="element-618" data-mp="18paxqs8upxim">{icon}</span>
                <h3 className="mt-5 font-bold text-[19px] sm:text-[22px] leading-snug text-ink" data-id="element-619" data-mp="13sao67csmcmr">{t}</h3>
                <p className="mt-2 text-[15px] sm:text-[16px] leading-relaxed text-gray-600" data-id="element-620" data-mp="3ak34vjje9jyo">{p}</p>
              </article>)}
          </div>
        </Wrapper>
      </section>

      {/* FINAL CTA */}
      <section className={SECTION} data-id="element-621" data-mp="1l37l3qeqj880">
        <Wrapper className="text-center" data-id="element-622" data-mp="1q0g8c5eqq2g4">
          <div className="inline-flex items-center rounded-full bg-brand px-5 py-2 text-[14px] sm:text-[16px] font-bold uppercase tracking-wide text-white" data-id="element-623" data-mp="1pz51txtwuglb">100% gratis</div>
          <h2 className={`${H2} mt-6`} data-id="element-624" data-mp="1g93fbah7h68i">Tu llamada con Chale no tiene costo</h2>
          <p className={`${LEAD} mt-5 max-w-[680px] mx-auto`} data-id="element-625" data-mp="129u993mprsi9">
            Si agendas <span className="font-semibold text-brand" data-id="element-626" data-mp="16byzrthv890o">esta semana</span>, te llevas <span className="hl-yellow font-semibold text-ink" data-id="element-627" data-mp="16da53xqylotw">el mapa de los primeros 90 días</span> que yo estoy siguiendo.
          </p>
          <ScheduleCountdown data-id="element-628" data-mp="xb0tf4jyfpex" />
          <HostRow data-id="element-629" data-mp="xcbwt39hmv5i" />
          <div className="mt-10" data-id="element-630" data-mp="xbsnuc1tfh3m"><CtaButton onClick={scrollToForm} data-id="element-631" data-mp="xd3tyiiqh0xw" /></div>
          <div className="mt-5" data-id="element-632" data-mp="2mr8pl6deu0jh"><TicketLine data-id="element-633" data-mp="2mqqi1dlarzw9" /></div>
        </Wrapper>
      </section>

      <Footer data-id="element-634" data-mp="2rpjxvco242zl" />
    </div>;
}